import { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext.jsx';
import { api } from '../lib/api.js';
import { copyText } from '../lib/util.js';
import { SKYAD_CATEGORIES } from '../lib/constants.js';
import { FormatPreview } from '../components/FormatPreview.jsx';
import { Modal } from '../components/Modal.jsx';
import { Btn, Field, MonoInput, PageHeading } from '../components/ui.jsx';

export default function Skyads() {
  const { snapshot, token, pushToast, refresh } = useApp();
  const isManager = Boolean(token);

  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState(null); // the snippet being edited, or null
  const [form, setForm] = useState({ category: SKYAD_CATEGORIES[0], title: '', content: '' });
  const [busy, setBusy] = useState(false);

  const openAdd = () => {
    setEdit(null);
    setForm({ category: SKYAD_CATEGORIES[0], title: '', content: '' });
    setAddOpen(true);
  };

  const openEdit = (ad) => {
    setEdit(ad);
    setForm({
      category: SKYAD_CATEGORIES.includes(ad.category) ? ad.category : SKYAD_CATEGORIES[0],
      title: ad.title || '',
      content: ad.content,
    });
    setAddOpen(true);
  };

  const grouped = useMemo(() => {
    const map = {};
    for (const c of SKYAD_CATEGORIES) map[c] = [];
    for (const ad of snapshot.skyads || []) {
      const key = SKYAD_CATEGORIES.includes(ad.category) ? ad.category : SKYAD_CATEGORIES[0];
      map[key].push(ad);
    }
    return map;
  }, [snapshot.skyads]);

  const totalAds = (snapshot.skyads || []).length;

  const handleCopy = async (content) => {
    const ok = await copyText(content);
    pushToast(ok ? 'Copied!' : 'Copy failed', ok ? 'teal' : 'ember');
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteSkyad(id, token);
      pushToast('Snippet deleted');
      refresh();
    } catch (err) {
      pushToast(err.message, 'ember');
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.content.trim() || busy) return;
    setBusy(true);
    try {
      const payload = { category: form.category, title: form.title.trim() || null, content: form.content };
      if (edit) {
        await api.updateSkyad(edit.id, payload, token);
        pushToast('Snippet updated');
      } else {
        await api.addSkyad(payload, token);
        pushToast('Snippet added');
      }
      setAddOpen(false);
      setEdit(null);
      refresh();
    } catch (err) {
      pushToast(err.message, 'ember');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeading
        eyebrow="Copy Bank · /ad"
        title="Skyads"
        live
        actions={
          <>
            <span className="font-mono text-[10px] uppercase tracking-widest text-dim">
              {totalAds} snippet{totalAds === 1 ? '' : 's'}
            </span>
            {isManager && (
              <Btn variant="teal" onClick={openAdd}>
                + Add New Snippet
              </Btn>
            )}
          </>
        }
      />

      {SKYAD_CATEGORIES.map((cat) => {
        const ads = grouped[cat] || [];
        return (
          <section key={cat} className="mb-10">
            <h2 className="mb-3 border-b border-edge pb-2 font-mono text-sm font-bold uppercase tracking-[0.3em] text-mist">
              {cat}
              <span className="ml-2 text-[10px] text-dim">({ads.length})</span>
            </h2>

            {ads.length === 0 ? (
              <div className="border border-dashed border-raise bg-surface/50 p-6 text-center font-mono text-[11px] uppercase tracking-widest text-dim">
                No snippets in this category
              </div>
            ) : (
              <div className="space-y-4">
                {ads.map((ad) => (
                  <article key={ad.id} className="border border-edge bg-panel p-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <h3 className="font-mono text-base font-bold uppercase tracking-widest text-snow">
                        {ad.title || 'Untitled Snippet'}
                      </h3>
                      <div className="flex items-center gap-2">
                        {isManager && (
                          <>
                            <Btn size="sm" variant="ghost" onClick={() => openEdit(ad)}>
                              Edit
                            </Btn>
                            <Btn size="sm" variant="ember-outline" onClick={() => handleDelete(ad.id)}>
                              Delete
                            </Btn>
                          </>
                        )}
                        <Btn size="sm" variant="teal" onClick={() => handleCopy(ad.content)}>
                          Copy Snippet
                        </Btn>
                      </div>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="border border-edge bg-void p-3">
                        <p className="mb-2 font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-dim">
                          Raw snippet
                        </p>
                        <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-mist">
                          {ad.content}
                        </pre>
                      </div>
                      <div className="border border-edge bg-void bg-grid p-3">
                        <p className="mb-2 font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-dim">
                          Live in-game preview
                        </p>
                        <FormatPreview
                          content={ad.content}
                          className="font-mono text-sm leading-relaxed"
                        />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        );
      })}

      {isManager && (
        <Modal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          title={edit ? 'Edit Skyad Snippet' : 'New Skyad Snippet'}
          accent="teal"
          wide
          footer={
            <>
              <Btn variant="ghost" onClick={() => setAddOpen(false)}>
                Cancel
              </Btn>
              <Btn
                type="submit"
                form="new-skyad-form"
                variant="teal"
                disabled={!form.content.trim() || busy}
              >
                {busy ? 'Saving…' : edit ? 'Save Changes' : 'Add Snippet'}
              </Btn>
            </>
          }
        >
          <form id="new-skyad-form" onSubmit={handleSave} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Category">
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full border border-edge bg-void px-3 py-2.5 font-mono text-sm text-snow outline-none focus:border-neon focus:shadow-neon-teal"
                >
                  {SKYAD_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Title (optional)">
                <MonoInput
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Open All Day"
                  maxLength={60}
                />
              </Field>
            </div>
            <Field
              label="Raw snippet content"
              hint="Caret codes: ^1–^9 colors · ^* bold · ^_ underline · ^~ strike · ^= underline+strike · ^r reset."
            >
              <textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={4}
                placeholder="^2Open now! ^*^=Strawberry Gas^r ^2is selling ^9^_Strawberry Water^r"
                className="w-full resize-y border border-edge bg-void px-3 py-2.5 font-mono text-sm leading-relaxed text-mist outline-none placeholder:text-dim focus:border-neon focus:shadow-neon-teal"
              />
            </Field>

            <div className="border border-edge bg-void bg-grid p-3">
              <p className="mb-2 font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-dim">
                Live in-game preview
              </p>
              <FormatPreview
                content={form.content}
                className="font-mono text-sm leading-relaxed"
              />
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}